/**
 * API client.
 *
 * Responsibilities that belong here and nowhere else:
 *
 *   * Attaching the bearer token, and refreshing it silently when it expires.
 *     A salesperson mid-order must never be bounced to a login screen because
 *     an access token aged out sixty seconds ago.
 *
 *   * Deciding what is retryable. A 4xx is the caller's problem and is thrown
 *     as a typed error; a 5xx or a dead network is transient.
 *
 *   * Read-through caching, so screens open with data while offline.
 */

import * as idb from './idb';
import { compress } from './camera';
import { withBusy } from './busy';
/**
 * STATIC ON PURPOSE, THOUGH IT USED TO BE `await import('./outbox')`.
 *
 * The dynamic form was there to keep outbox code out of the initial bundle,
 * and it achieved nothing: outbox.ts is statically imported by main.tsx, the
 * Shell and nine screens, so it is in the main chunk regardless. Vite said so
 * on every build — "dynamic import will not move module into another chunk".
 *
 * It is safe because outbox.ts imports only ./idb, so there is no cycle back
 * to this module. If outbox.ts ever needs something from api.ts, this becomes
 * a real import cycle and must go back to being lazy.
 */
import { getStashedMedia } from './outbox';

/**
 * In production the app is served from Cloudflare Pages and the API from a
 * Worker on a different origin, so the base URL is injected at build time. In
 * development Vite proxies /api, so a relative path keeps everything
 * same-origin and CORS never comes up.
 */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export interface ApiErrorShape {
  code: string;
  message: string;
  fields?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly fields?: Record<string, string>,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the same request could succeed later. */
  get isTransient(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }

  /** True when the session is gone and the user must sign in again. */
  get isAuthFailure(): boolean {
    return this.status === 401
      && [
        'UNAUTHORIZED', 'INVALID_TOKEN', 'TOKEN_EXPIRED', 'DEVICE_NOT_REGISTERED',
        // A revoked device must drop the local session immediately. Left out
        // of this list, the app would keep the tokens and retry forever
        // against a device the server has already cut off — the six-month
        // session makes that a six-month retry loop.
        'DEVICE_REVOKED',
      ].includes(this.code);
  }
}

/**
 * The message to actually SHOW the user from a failed request.
 *
 * A validation failure (422) carries its reason in `fields` — e.g.
 * `{ licence: "Your driving licence expired on 2024-06-30. The office must
 * update it before you can start your day." }` — while `message` is only the
 * generic summary "Please correct the highlighted fields". A screen that has no
 * per-field display (My Day has no "licence" input) was showing that summary and
 * nothing else, so a driver saw a red banner pointing at nothing. This returns
 * the field reasons when there are any, so the real cause always reaches the
 * person, and falls back to the message, then to `fallback` for a non-API error.
 */
export function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.fields) {
      const reasons = Object.values(err.fields).filter((m) => !!m && m.trim().length);
      if (reasons.length) return reasons.join(' ');
    }
    return err.message || fallback;
  }
  return fallback;
}

interface Session {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: {
    id: string;
    fullName: string;
    phone: string;
    role: string;
    locale: 'en' | 'hi';
    salespersonId: string | null;
    driverId: string | null;
    customerId: string | null;
  };
  deviceFingerprint: string;
}

let session: Session | null = null;
let refreshInFlight: Promise<void> | null = null;
const authListeners = new Set<(s: Session | null) => void>();

export function onAuthChange(fn: (s: Session | null) => void): () => void {
  authListeners.add(fn);
  return () => authListeners.delete(fn);
}

function emitAuth(): void {
  for (const fn of authListeners) fn(session);
}

export function getSession(): Session | null {
  return session;
}

export async function loadSession(): Promise<Session | null> {
  session = (await idb.kvGet<Session>('session')) ?? null;
  emitAuth();
  return session;
}

export async function setSession(next: Session | null): Promise<void> {
  session = next;
  if (next) await idb.kvSet('session', next);
  else await idb.kvDel('session');
  emitAuth();
}

/**
 * A stable per-install identifier.
 *
 * The server binds a device to the user: OTP registers the device, and a PIN
 * logs in on it thereafter. A correct PIN from an unknown fingerprint is
 * refused and forced back through OTP — which is what stops a leaked 4-digit
 * PIN being enough on its own.
 */
export async function deviceFingerprint(): Promise<string> {
  let fp = await idb.kvGet<string>('deviceFingerprint');
  if (!fp) {
    fp = crypto.randomUUID();
    await idb.kvSet('deviceFingerprint', fp);
  }
  return fp;
}

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------

/**
 * Refresh the access token.
 *
 * Concurrent callers share one in-flight refresh. Without that, five parallel
 * requests hitting an expired token would fire five refreshes — and because
 * refresh tokens ROTATE, four of them would present an already-used token,
 * which the server treats as theft and revokes the whole chain. That would log
 * the user out for doing nothing wrong.
 */
async function refresh(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  if (!session?.refreshToken) throw new ApiError('UNAUTHORIZED', 'Not signed in', 401);

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: session!.refreshToken }),
      });
      const json = await res.json() as { ok: boolean; data?: Record<string, unknown>; error?: ApiErrorShape };

      if (!res.ok || !json.ok || !json.data) {
        await setSession(null);
        throw new ApiError(json.error?.code ?? 'INVALID_TOKEN', json.error?.message ?? 'Session expired', 401);
      }

      const d = json.data as {
        accessToken: string; refreshToken: string; expiresIn: number; user: Session['user'];
      };
      await setSession({
        accessToken: d.accessToken,
        refreshToken: d.refreshToken,
        expiresAt: Date.now() + (d.expiresIn - 60) * 1000,
        user: d.user ?? session!.user,
        deviceFingerprint: session!.deviceFingerprint,
      });
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip the Authorization header (login, bootstrap). */
  anonymous?: boolean;
  /** Cache the response under this key and serve it when offline. */
  cacheKey?: string;
  cacheTtlSeconds?: number;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  // Refresh proactively rather than waiting for a 401 — one fewer round trip,
  // and it keeps a slow shop connection from paying for two requests.
  if (!options.anonymous && session && session.expiresAt <= Date.now()) {
    try { await refresh(); } catch { /* fall through; the 401 path handles it */ }
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!options.anonymous && session) headers.Authorization = `Bearer ${session.accessToken}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (err) {
    // Network failure. For a GET, stale cache is better than an error screen.
    if (method === 'GET' && options.cacheKey) {
      const cached = await idb.cacheGet<T>(options.cacheKey);
      if (cached) return cached.value;
    }
    throw new ApiError(
      'NETWORK_ERROR',
      navigator.onLine ? 'Could not reach the server' : 'You are offline',
      0,
    );
  }

  // A 401 on the first attempt means the token died between the check above and
  // the request. Refresh once and retry — but only once.
  if (res.status === 401 && !options.anonymous && session) {
    try {
      await refresh();
      return request<T>(path, options);
    } catch {
      throw new ApiError('UNAUTHORIZED', 'Please sign in again', 401);
    }
  }

  const requestId = res.headers.get('X-Request-Id') ?? undefined;

  // Non-JSON responses (PDF, CSV, SVG) are returned as a Blob.
  const contentType = res.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!res.ok) throw new ApiError('REQUEST_FAILED', `Request failed (${res.status})`, res.status, undefined, requestId);
    return (await res.blob()) as unknown as T;
  }

  const json = await res.json() as { ok: boolean; data?: T; error?: ApiErrorShape };

  if (!res.ok || !json.ok) {
    throw new ApiError(
      json.error?.code ?? 'REQUEST_FAILED',
      json.error?.message ?? `Request failed (${res.status})`,
      res.status,
      json.error?.fields,
      requestId,
    );
  }

  if (method === 'GET' && options.cacheKey) {
    await idb.cacheSet(options.cacheKey, json.data, options.cacheTtlSeconds ?? 3600);
  }

  return json.data as T;
}

// ---------------------------------------------------------------------------
// Convenience verbs
// ---------------------------------------------------------------------------

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  // Mutations are flagged busy so the branded spinner shows over the screen while
  // they run (a save, an allocate, a status change). GETs are not, so background
  // polls and refetches never flash a spinner.
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    withBusy(() => request<T>(path, { ...opts, method: 'POST', body })),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    withBusy(() => request<T>(path, { ...opts, method: 'PATCH', body })),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    withBusy(() => request<T>(path, { ...opts, method: 'PUT', body })),
  del: <T>(path: string, opts?: RequestOptions) =>
    withBusy(() => request<T>(path, { ...opts, method: 'DELETE' })),
};

/**
 * GET that prefers cache while offline and reports staleness.
 *
 * Screens use this to render immediately with whatever is stored, then update
 * when the network answers.
 */
export async function getCached<T>(
  path: string,
  cacheKey: string,
  ttlSeconds = 3600,
): Promise<{ data: T; stale: boolean; fromCache: boolean }> {
  if (!navigator.onLine) {
    const cached = await idb.cacheGet<T>(cacheKey);
    if (cached) return { data: cached.value, stale: cached.stale, fromCache: true };
  }
  try {
    const data = await api.get<T>(path, { cacheKey, cacheTtlSeconds: ttlSeconds });
    return { data, stale: false, fromCache: false };
  } catch (err) {
    const cached = await idb.cacheGet<T>(cacheKey);
    if (cached) return { data: cached.value, stale: true, fromCache: true };
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Media upload
// ---------------------------------------------------------------------------

export interface UploadResult {
  mediaId: string;
  key: string;
  url: string;
  sizeBytes: number;
  mimeType: string;
  compressed: boolean;
  savedBytes?: number;
}

export async function uploadMedia(params: {
  blob: Blob;
  purpose: string;
  entityType?: string;
  entityId?: string;
  originalSizeBytes?: number;
  widthPx?: number;
  heightPx?: number;
  filename?: string;
}): Promise<UploadResult> {
  // Flag busy across compress + upload so the branded spinner shows while a photo
  // or document is going up (the slowest thing a field user waits on).
  return withBusy(() => uploadMediaInner(params));
}

async function uploadMediaInner(params: {
  blob: Blob;
  purpose: string;
  entityType?: string;
  entityId?: string;
  originalSizeBytes?: number;
  widthPx?: number;
  heightPx?: number;
  filename?: string;
}): Promise<UploadResult> {
  if (!session) throw new ApiError('UNAUTHORIZED', 'Not signed in', 401);

  // Compress ANY sizeable image here, not only camera captures. A photo picked
  // from the gallery, pasted, or a KYC document shot in a file picker used to go
  // up at full phone-camera size (4–8 MB) over shop 4G — slow enough that the
  // screen sat "busy" and people gave up. Doing it in uploadMedia means every
  // caller is covered. Camera captures are already ~200–400 KB so they skip the
  // threshold; non-images and a failed canvas fall through to the original.
  let blob = params.blob;
  let widthPx = params.widthPx;
  let heightPx = params.heightPx;
  let originalSizeBytes = params.originalSizeBytes;
  if (blob.type.startsWith('image/') && blob.size > 600_000) {
    try {
      const c = await compress(blob);
      originalSizeBytes = originalSizeBytes ?? c.originalSizeBytes;
      blob = c.blob;
      widthPx = c.width;
      heightPx = c.height;
    } catch { /* keep the original blob if the canvas/decoder is unavailable */ }
  }

  const form = new FormData();
  form.append('file', blob, params.filename ?? 'upload.jpg');
  form.append('purpose', params.purpose);
  if (params.entityType) form.append('entityType', params.entityType);
  if (params.entityId) form.append('entityId', params.entityId);
  if (originalSizeBytes) form.append('originalSizeBytes', String(originalSizeBytes));
  if (widthPx) form.append('widthPx', String(widthPx));
  if (heightPx) form.append('heightPx', String(heightPx));

  const res = await fetch(`${API_BASE}/api/media/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.accessToken}` },
    body: form,
  });

  const json = await res.json() as { ok: boolean; data?: UploadResult; error?: ApiErrorShape };
  if (!res.ok || !json.ok || !json.data) {
    throw new ApiError(
      json.error?.code ?? 'UPLOAD_FAILED',
      json.error?.message ?? 'Upload failed',
      res.status,
      json.error?.fields,
    );
  }
  return json.data;
}

/**
 * POST a FormData body to any endpoint.
 *
 * `api.post` sets Content-Type: application/json, which corrupts a multipart
 * body — the browser must set that header itself so it can include the
 * boundary. `uploadMedia` above solves this only for /api/media/upload; this
 * is the same thing for endpoints that take a file and are not that one, such
 * as the company signature.
 */
export async function postForm<T>(path: string, body: FormData): Promise<T> {
  if (!session) throw new ApiError('UNAUTHORIZED', 'Not signed in', 401);

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    // Authorization only. Deliberately no Content-Type.
    headers: { Authorization: `Bearer ${session.accessToken}` },
    body,
  });

  const json = await res.json() as { ok: boolean; data?: T; error?: ApiErrorShape };
  if (!res.ok || !json.ok || json.data === undefined) {
    throw new ApiError(
      json.error?.code ?? 'UPLOAD_FAILED',
      json.error?.message ?? 'Upload failed',
      res.status,
      json.error?.fields,
    );
  }
  return json.data;
}

/**
 * Open an authenticated PDF in a new tab.
 *
 * THE BUG THIS FIXES
 *
 * Invoice PDFs were linked with a plain `<a href="{API}/api/invoices/x/pdf">`.
 * Every route under /api/invoices requires a bearer token, and an anchor tag
 * cannot send an Authorization header — so "View invoice" returned 401 for
 * every user, always. It was never caught because no retailer had ever tapped
 * it: every screen had only been opened as an admin, in a browser tab, by the
 * person who wrote it.
 *
 * WHY THE TAB IS OPENED FIRST
 *
 * `window.open` is only allowed while the browser still considers itself
 * inside a user gesture. Awaiting the fetch first and opening afterwards gets
 * the call classified as a popup and blocked — the tap would do nothing at
 * all, which looks exactly like the 401 it replaced. So the tab is claimed
 * synchronously and its location set once the bytes arrive.
 */
export async function openPdf(path: string): Promise<void> {
  const tab = window.open('', '_blank');

  try {
    const url = await fetchBlob(path);
    if (tab) {
      tab.location.href = url;
    } else {
      // Popup blocked despite the gesture, or an in-app webview that refuses
      // new tabs. Same-tab navigation is worse but it works, and the back
      // button returns the user to the app.
      window.location.href = url;
    }
    // The object URL is not revoked: doing so races the new tab's load and
    // produces a blank viewer. A handful of leaked blob URLs die with the
    // page, which is a better trade than a document that will not open.
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/**
 * POST a file to an arbitrary endpoint, as multipart form data.
 *
 * `uploadMedia` above always posts to /api/media/upload, which stores a file
 * and hands back a key. Some endpoints need the BYTES themselves — the product
 * picture goes straight on to Business Central and only then becomes a stored
 * file — so they take the upload directly.
 *
 * No Content-Type header is set, deliberately: the browser has to add its own
 * multipart boundary, and setting the header by hand produces a body the server
 * cannot parse, with an error that says nothing about why.
 */
export async function postFile<T>(
  path: string,
  file: Blob,
  filename = 'upload.jpg',
  /**
   * Extra form fields sent alongside the file.
   *
   * `/api/media/upload` reads its `purpose` from the FORM, not the query
   * string — a request that put it in the URL was accepted by fetch, arrived
   * with no purpose at all, and was rejected with a validation error naming
   * every legal value. The field has to go here.
   */
  fields?: Record<string, string>,
): Promise<T> {
  if (!session) throw new ApiError('UNAUTHORIZED', 'Not signed in', 401);

  const form = new FormData();
  form.append('file', file, filename);
  for (const [k, v] of Object.entries(fields ?? {})) form.append(k, v);

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.accessToken}` },
    body: form,
  });

  const json = await res.json() as { ok: boolean; data?: T; error?: ApiErrorShape };
  if (!res.ok || !json.ok || json.data === undefined) {
    throw new ApiError(
      json.error?.code ?? 'UPLOAD_FAILED',
      json.error?.message ?? 'Upload failed',
      res.status,
      json.error?.fields,
    );
  }
  return json.data;
}

/**
 * A cache-busting token for media URLs.
 *
 * WHY A CONSTANT IN THE URL IS THE RIGHT TOOL HERE
 *
 * Media is served with `max-age=31536000, immutable` and is also stored by the
 * service worker, both keyed on the URL. That is correct — the R2 key contains
 * a content hash, so the bytes at a URL never change.
 *
 * The HEADERS can change, and on 28 Sep they had to: responses were missing
 * `Cross-Origin-Resource-Policy`, so every image was fetched successfully and
 * then blocked by the browser. Fixing the server changed nothing, because the
 * header-less responses were already cached — as 200s, since the block happens
 * at the consumer, not on the wire. Nothing on the server can reach a response
 * that is never requested again.
 *
 * Bumping this changes every media URL once, which retires every poisoned
 * entry in every browser and every service worker without asking anybody to
 * clear anything. Caching then resumes exactly as before.
 *
 * Bump it only when the RESPONSE HEADERS change. Changing the bytes does not
 * need it; those get a new key.
 */
const MEDIA_CACHE_BUST = '2';

/** Absolute URL for a media path returned by the API. */
export function mediaUrl(pathOrKey: string): string {
  if (pathOrKey.startsWith('http')) return pathOrKey;

  const base = pathOrKey.startsWith('/api/')
    ? `${API_BASE}${pathOrKey}`
    : `${API_BASE}/api/media/${encodeURIComponent(pathOrKey)}`;

  return `${base}${base.includes('?') ? '&' : '?'}v=${MEDIA_CACHE_BUST}`;
}

/**
 * Authenticated blob fetch, for PDFs and images that need the bearer token.
 *
 * An <img src> or <a href> cannot carry an Authorization header, so protected
 * media is fetched here and handed to the DOM as an object URL.
 */
export async function fetchBlob(path: string): Promise<string> {
  if (!session) throw new ApiError('UNAUTHORIZED', 'Not signed in', 401);
  const res = await fetch(mediaUrl(path), {
    headers: { Authorization: `Bearer ${session.accessToken}` },
    /**
     * NEVER FROM THE BROWSER'S CACHE.
     *
     * A plain fetch uses the HTTP cache, and invoice PDFs used to be served
     * with max-age=86400. So once a browser had a copy it stopped ASKING for a
     * day — an invoice edited in the app or in Business Central went on opening
     * as the old document, and the server, which by then knew perfectly well
     * the PDF was stale, was never consulted.
     *
     * The server's header was corrected to no-cache on 20 Sep, and that alone
     * fixed nothing: the already-stored responses carried the old max-age and
     * stayed fresh for a day regardless. A header only governs responses that
     * arrive after it.
     *
     * no-store here is the part that cannot be undone by something already in
     * the cache. These are tax documents opened a handful of times a day; there
     * is no version of this trade where saving a request is worth handing
     * somebody an invoice that no longer matches the books.
     */
    cache: 'no-store',
  });
  if (!res.ok) throw new ApiError('NOT_FOUND', 'File not available', res.status);
  return URL.createObjectURL(await res.blob());
}

// ---------------------------------------------------------------------------
// Outbox sender
// ---------------------------------------------------------------------------

/**
 * Send one queued item.
 *
 * Photos captured offline are uploaded FIRST, and the returned media id is
 * substituted into the payload. A POD whose photo failed to upload must not be
 * sent, because the server refuses a POD without a real photo — and a queued
 * item that can never succeed would sit in the outbox forever.
 */
export async function sendOutboxItem(item: {
  method: string; path: string; body: unknown; mediaIds?: string[];
}): Promise<{ ok: true } | { ok: false; permanent: boolean; message: string; code?: string }> {
  try {
    const body = { ...(item.body as Record<string, unknown>) };

    if (item.mediaIds?.length) {
      const uploadedIds: string[] = [];

      for (const localId of item.mediaIds) {
        const stashed = await getStashedMedia(localId);
        if (!stashed) continue;
        const result = await uploadMedia({
          blob: stashed.blob,
          purpose: stashed.purpose,
          filename: `${localId}.jpg`,
        });
        uploadedIds.push(result.mediaId);
      }

      // Substitute real media ids for the local placeholders.
      if (body.photoMediaId && uploadedIds[0]) body.photoMediaId = uploadedIds[0];
      if (body.proofMediaId && uploadedIds[0]) body.proofMediaId = uploadedIds[0];
      if (Array.isArray(body.photoMediaIds)) body.photoMediaIds = uploadedIds;
    }

    await request(item.path, { method: item.method as 'POST', body });
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError) {
      // A duplicate is a success: the server already has this record, and the
      // idempotency key is exactly what makes that determination possible.
      if (err.code === 'DUPLICATE' || err.code === 'CONFLICT') return { ok: true };
      return {
        ok: false,
        permanent: !err.isTransient && !err.isAuthFailure,
        message: err.message,
        code: err.code,
      };
    }
    return { ok: false, permanent: false, message: 'Unexpected error' };
  }
}
